import { useRef, useState } from 'react'
import Papa from 'papaparse'
import { TrashIcon, PlusIcon, UploadIcon, DocumentDownloadIcon } from '@heroicons/react/outline'

import { findColumnByName, generateCsvTemplate } from '../../utils/csvUtils'

// Stable per-row id so React keys survive row deletion/reorder (index keys cause
// focus/cursor jumps while editing). Shared with parents that seed rows.
let ridSeq = 0
export const makeRowId = () => `r${++ridSeq}`

/**
 * Generic "paste/upload CSV -> editable table" grid used by the shipping-costs
 * bulk wizards. It owns import + inline editing + per-cell validation; the parent
 * owns the row array and reads validity back through onChange.
 *
 * Props:
 *   columns       – [{ key, label, type: "number"|"text"|"bool", required, min, step,
 *                      placeholder, width, parse, prefix, suffix }] — `parse(rawCell)`
 *                      optionally normalizes an imported cell (e.g. a bool column maps
 *                      TRUE/1/sí/sim onto the tri-state '' / 'true' / 'false'); `prefix` /
 *                      `suffix` render a static unit adornment inside the cell (e.g. a
 *                      currency prefix on a cost, a "kg" suffix on a weight)
 *   rows          – array of row objects keyed by column.key (parent-owned state)
 *   onChange      – (rows) => void
 *   validateRow   – optional (row, index, rows) => ({ [key]: message }) for
 *                   cross-field / async-free custom rules, merged over the
 *                   built-in per-column rules
 *   templateHeaders / templateFilename – enable the "download template" button
 *   carryOverKeys – column keys a new ("add row") row inherits from the last row
 *                   (e.g. the zone name when adding another weight tier)
 *   t / tPrefix   – i18n; keys read under `${tPrefix}.*` (default "csv_grid")
 *
 * A row is invalid when any built-in or custom rule returns a message. Parents
 * compute overall validity with the exported `gridRowErrors` helper so the dry-run
 * button can be gated without re-implementing the rules.
 */

// Built-in per-column validation, shared with parents via export so the submit
// gate and the inline cell errors never drift apart.
export const gridColumnError = (column, value) => {
  const raw = value === undefined || value === null ? '' : String(value).trim()
  if (!raw) return column.required ? 'required' : null
  if (column.type === 'number') {
    const num = Number(raw)
    if (Number.isNaN(num)) return 'not_a_number'
    if (column.min !== undefined && num < column.min) return 'too_small'
    if (column.gt !== undefined && num <= column.gt) return 'not_positive'
  }
  return null
}

export const gridRowErrors = (columns, row, index, rows, validateRow) => {
  const errors = {}
  columns.forEach(col => {
    const err = gridColumnError(col, row[col.key])
    if (err) errors[col.key] = err
  })
  if (validateRow) {
    const custom = validateRow(row, index, rows) || {}
    Object.keys(custom).forEach(k => {
      if (custom[k]) errors[k] = custom[k]
    })
  }
  return errors
}

export const CsvEditableGrid = ({
  columns,
  rows,
  onChange,
  validateRow,
  templateHeaders,
  templateFilename = 'template.csv',
  carryOverKeys = [],
  hideImport = false,
  t,
  tPrefix = 'csv_grid',
}) => {
  const fileInputRef = useRef(null)
  const [pasteOpen, setPasteOpen] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const [parseError, setParseError] = useState(null)

  const tr = (key, opts) => t(`${tPrefix}.${key}`, opts)
  const cellErrorText = code => (code ? tr(`errors.${code}`) : null)

  // Map parsed CSV records (header-keyed) onto the grid columns. Header matching
  // is case-insensitive so "ID"/"id" and "Cost"/"cost" both land correctly.
  const mapRecords = (records, fields) =>
    records.map(rec => {
      const row = { __rid: makeRowId() }
      columns.forEach(col => {
        const header = findColumnByName(fields, col.key) || findColumnByName(fields, col.label)
        const raw = header ? String(rec[header] ?? '').trim() : ''
        // A column may declare a `parse` fn to normalize its imported value onto the domain
        // the cell editor understands (e.g. a bool column onto the tri-state ''/'true'/'false');
        // otherwise the trimmed raw value is stored as-is.
        row[col.key] = col.parse ? col.parse(raw) : raw
      })
      return row
    })

  const ingest = (records, fields) => {
    const mapped = mapRecords(records, fields).filter(row => columns.some(col => row[col.key] !== ''))
    if (mapped.length === 0) {
      setParseError(tr('errors.empty'))
      return
    }
    setParseError(null)
    onChange(mapped)
  }

  // Reject a genuinely garbled parse (quote/structure corruption that shifts fields) instead of
  // silently ingesting mis-mapped rows, but tolerate PapaParse's non-fatal warnings — a ragged or
  // short row (FieldMismatch) and delimiter auto-detection (Delimiter) — so one bad trailing row
  // doesn't reject an otherwise-valid file. Field mapping is header-keyed, so a row missing/adding a
  // column doesn't mis-map the named columns.
  const ingestParsed = res => {
    const fatalErrors = (res.errors || []).filter(e => e.type !== 'FieldMismatch' && e.type !== 'Delimiter')
    if (fatalErrors.length > 0) {
      setParseError(tr('errors.parse_issues', { count: fatalErrors.length }))
      return false
    }
    ingest(res.data, res.meta.fields || [])
    return true
  }

  const handleFile = file => {
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setParseError(tr('errors.csv_only'))
      return
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: res => ingestParsed(res),
      error: () => setParseError(tr('errors.parse_failed')),
    })
  }

  const handlePaste = () => {
    const text = pasteText.trim()
    if (!text) return
    const res = Papa.parse(text, { header: true, skipEmptyLines: true })
    if (ingestParsed(res)) {
      setPasteOpen(false)
      setPasteText('')
    }
  }

  const updateCell = (index, key, value) => {
    const next = rows.map((row, i) => (i === index ? { ...row, [key]: value } : row))
    onChange(next)
  }

  const addRow = () => {
    // Carry over selected fields (e.g. the zone / currency) from the last row so
    // adding another tier for the same zone doesn't force retyping the name.
    const last = rows[rows.length - 1]
    const blank = { __rid: makeRowId() }
    columns.forEach(col => {
      blank[col.key] = last && carryOverKeys.includes(col.key) ? (last[col.key] ?? '') : ''
    })
    onChange([...rows, blank])
  }

  const removeRow = index => onChange(rows.filter((_, i) => i !== index))

  return (
    <div className="space-y-3">
      {/* Import toolbar (hidden when the host provides its own upload step) */}
      {!hideImport && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={e => {
              handleFile(e.target.files?.[0])
              e.target.value = ''
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <UploadIcon className="h-4 w-4" />
            {tr('upload')}
          </button>
          <button
            type="button"
            onClick={() => setPasteOpen(v => !v)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            {tr('paste')}
          </button>
          {templateHeaders && (
            <button
              type="button"
              onClick={() => generateCsvTemplate(templateFilename, templateHeaders)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <DocumentDownloadIcon className="h-4 w-4" />
              {tr('download_template')}
            </button>
          )}
        </div>
      )}

      {!hideImport && pasteOpen && (
        <div className="space-y-2">
          <textarea
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
            rows={5}
            placeholder={tr('paste_placeholder')}
            className="w-full rounded-md border border-gray-300 p-2 font-mono text-xs focus:border-indigo-500 focus:ring-indigo-500"
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handlePaste}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {tr('paste_apply')}
            </button>
          </div>
        </div>
      )}

      {parseError && <p className="text-sm text-red-600">{parseError}</p>}

      {/* Editable table */}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-10 px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">#</th>
                {columns.map(col => (
                  <th
                    key={col.key}
                    className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500"
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.label}
                    {col.required && <span className="text-red-500"> *</span>}
                  </th>
                ))}
                <th className="w-10 px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((row, index) => {
                const errors = gridRowErrors(columns, row, index, rows, validateRow)
                return (
                  <tr key={row.__rid ?? index}>
                    <td className="px-3 py-1.5 text-xs text-gray-400">{index + 1}</td>
                    {columns.map(col => {
                      const err = errors[col.key]
                      const cellClass = `w-full rounded-md border px-2 py-1 text-sm focus:ring-1 ${
                        err ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                      }`
                      return (
                        <td key={col.key} className="px-3 py-1.5 align-top">
                          {col.type === 'bool' ? (
                            // Tri-state: unset (don't send) / true / false. Labels come
                            // from the column so the grid stays domain-agnostic.
                            <select
                              value={row[col.key] ?? ''}
                              onChange={e => updateCell(index, col.key, e.target.value)}
                              className={cellClass}
                              style={{ minWidth: '11rem' }}
                            >
                              <option value="">{col.boolLabels?.unset ?? '—'}</option>
                              <option value="true">{col.boolLabels?.yes ?? 'Yes'}</option>
                              <option value="false">{col.boolLabels?.no ?? 'No'}</option>
                            </select>
                          ) : (
                            // Optional unit adornments (e.g. a currency prefix on a cost, a "kg"
                            // suffix on a weight) so the value reads with its unit of measure.
                            <div className="relative">
                              {col.prefix && (
                                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-xs text-gray-400">{col.prefix}</span>
                              )}
                              <input
                                type={col.type === 'number' ? 'number' : 'text'}
                                step={col.step}
                                value={row[col.key] ?? ''}
                                placeholder={col.placeholder}
                                onChange={e => updateCell(index, col.key, e.target.value)}
                                className={`${cellClass}${col.prefix ? ' pl-9' : ''}${col.suffix ? ' pr-8' : ''}`}
                              />
                              {col.suffix && (
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-xs text-gray-400">
                                  {col.suffix}
                                </span>
                              )}
                            </div>
                          )}
                          {err && <p className="mt-0.5 text-xs text-red-600">{cellErrorText(err)}</p>}
                        </td>
                      )
                    })}
                    <td className="px-3 py-1.5 text-center align-top">
                      <button type="button" onClick={() => removeRow(index)} className="p-1 text-gray-400 hover:text-red-600" title={tr('remove_row')}>
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <button type="button" onClick={addRow} className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700">
        <PlusIcon className="h-4 w-4" />
        {tr('add_row')}
      </button>
    </div>
  )
}
